import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { primary: '#2b3380', success: '#4caf50', warning: '#ff9800' };

function WhatsAppChatbotSettings({ user, onLogout }) {
  const [config, setConfig] = useState({
    enabled: true,
    model: 'gpt-5.1',
    provider: 'openai',
    system_prompt: '',
    auto_handoff_keywords: [],
    max_auto_responses: 5,
    knowledge_base: ''
  });
  const [stats, setStats] = useState({});
  const [logs, setLogs] = useState([]);
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchStats();
    fetchLogs();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/chatbot/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/chatbot/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/chatbot/logs?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/whatsapp/chatbot/config`,
        config,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('✅ Chatbot configuration saved successfully!');
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error saving configuration');
    }
    setLoading(false);
  };

  const handleTestChatbot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/whatsapp/chatbot/test`,
        { message: testMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResult(response.data);
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || 'Test failed' });
    }
    setLoading(false);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div style={{ padding: '24px' }}>
        <h1 style={{ color: COLORS.primary, marginBottom: '24px' }}>🤖 AI Chatbot Settings</h1>

        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Total Interactions</div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: COLORS.primary }}>{stats.total_interactions || 0}</div>
          </div>
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Handoffs to Human</div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: COLORS.warning }}>{stats.total_handoffs || 0}</div>
          </div>
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Automation Rate</div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: COLORS.success }}>{stats.automation_rate || 0}%</div>
          </div>
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Avg Responses/Conv</div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: COLORS.primary }}>{stats.avg_responses_per_conversation || 0}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Configuration Form */}
          <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ color: COLORS.primary, marginTop: 0 }}>Configuration</h3>
            <form onSubmit={handleSaveConfig}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={config.enabled}
                    onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                  />
                  <span style={{ fontWeight: '500' }}>Enable AI Chatbot</span>
                </label>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Model</label>
                <select
                  value={config.model}
                  onChange={(e) => setConfig({ ...config, model: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="gpt-5.1">GPT-5.1 (Best Quality)</option>
                  <option value="gpt-5">GPT-5</option>
                  <option value="claude-sonnet-4-5-20250929">Claude Sonnet 4</option>
                  <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>System Prompt</label>
                <textarea
                  value={config.system_prompt}
                  onChange={(e) => setConfig({ ...config, system_prompt: e.target.value })}
                  rows={4}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'inherit' }}
                  placeholder="You are a helpful customer service assistant..."
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Knowledge Base</label>
                <textarea
                  value={config.knowledge_base}
                  onChange={(e) => setConfig({ ...config, knowledge_base: e.target.value })}
                  rows={3}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'inherit' }}
                  placeholder="Product info, FAQs, policies..."
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Max Auto-Responses Before Handoff</label>
                <input
                  type="number"
                  value={config.max_auto_responses}
                  onChange={(e) => setConfig({ ...config, max_auto_responses: parseInt(e.target.value) })}
                  min="1"
                  max="20"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: COLORS.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Saving...' : '💾 Save Configuration'}
              </button>
            </form>
          </div>

          {/* Test Chatbot */}
          <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ color: COLORS.primary, marginTop: 0 }}>Test Chatbot</h3>
            <form onSubmit={handleTestChatbot}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Test Message</label>
                <textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  rows={3}
                  required
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'inherit' }}
                  placeholder="Type a test message..."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: COLORS.success,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  marginBottom: '16px'
                }}
              >
                {loading ? 'Testing...' : '🧪 Test Chatbot'}
              </button>
            </form>

            {testResult && (
              <div style={{
                padding: '16px',
                borderRadius: '6px',
                backgroundColor: testResult.success ? '#e8f5e9' : '#ffebee',
                border: `1px solid ${testResult.success ? '#4caf50' : '#f44336'}`
              }}>
                <div style={{ fontWeight: '600', marginBottom: '8px', color: testResult.success ? '#2e7d32' : '#c62828' }}>
                  {testResult.success ? '✅ Response' : '❌ Error'}
                </div>
                <div style={{ fontSize: '14px', color: '#333', whiteSpace: 'pre-wrap' }}>
                  {testResult.message}
                </div>
                {testResult.handoff && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#ff9800' }}>
                    🤝 Handed off to human agent
                  </div>
                )}
              </div>
            )}

            {/* Recent Interactions */}
            <div style={{ marginTop: '24px' }}>
              <h4 style={{ color: COLORS.primary, marginBottom: '12px' }}>Recent Interactions</h4>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {logs.map((log, idx) => (
                  <div key={idx} style={{ padding: '12px', borderBottom: '1px solid #e0e0e0', fontSize: '13px' }}>
                    <div style={{ fontWeight: '500', marginBottom: '4px' }}>{log.phone_number}</div>
                    <div style={{ color: '#666', marginBottom: '4px' }}>User: {log.user_message}</div>
                    <div style={{ color: COLORS.primary }}>AI: {log.ai_response?.substring(0, 100)}...</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default WhatsAppChatbotSettings;